/**
 * Live exam progress over Server-Sent Events.
 *
 * Replaces the Socket.IO hook. SSE is plain HTTP, so it carries the session
 * cookie with no separate handshake auth, and `EventSource` reconnects on its own.
 *
 * Two fixes over the old hook:
 *
 *   1. `connected` is real state, so the UI actually re-renders when the stream
 *      opens or drops. The old version returned it from a `useRef`, which never
 *      triggered a render — the connection indicator was permanently dead.
 *   2. `degraded` reports when the stream has failed repeatedly, which is the
 *      signal for the caller to lean on `useGradingPoller` instead. Previously the
 *      poller was silently carrying the whole feature in production, because
 *      worker-emitted events never reached the client at all.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Attempt, QuestionGradedEvent } from '@cwy/config/contracts';
import { useExamStore } from '@/store/examStore';

/** Consecutive failures after which we consider the stream unusable. */
const DEGRADE_AFTER = 3;

export interface ExamEventsState {
  connected: boolean;
  /** True when SSE keeps failing — fall back to bounded polling. */
  degraded: boolean;
}

export function useExamEvents(spId: string | null): ExamEventsState {
  const navigate = useNavigate();
  const [connected, setConnected] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const failures = useRef(0);

  const recordResult = useExamStore((s) => s.recordResult);
  const recordAttempt = useExamStore((s) => s.recordAttempt);
  const setOpenLadderGrade = useExamStore((s) => s.setOpenLadderGrade);

  useEffect(() => {
    if (!spId) return;

    const source = new EventSource(`/api/v1/events/student-papers/${spId}`, {
      withCredentials: true,
    });

    source.onopen = () => {
      failures.current = 0;
      setConnected(true);
      setDegraded(false);
    };

    source.onerror = () => {
      setConnected(false);
      failures.current += 1;
      if (failures.current >= DEGRADE_AFTER) setDegraded(true);
    };

    source.addEventListener('question-graded', (event) => {
      const evt = JSON.parse((event as MessageEvent<string>).data) as QuestionGradedEvent;

      // Wrong, ladder still open. For a TYPED/SCAN answer the attempt response was
      // only `{ pending, jobId }`, so this event is the first delivery of the
      // unlocked support tier — dropping it left the poller as the only channel.
      // The panel applies it: it holds the answer text this event does not carry.
      if (evt.finalBin === null) {
        setOpenLadderGrade({
          questionId: evt.questionId,
          attemptIndex: evt.attemptIndex,
          support: evt.support,
        });
        return;
      }

      if (evt.attempt) recordAttempt(evt.attempt);
      recordResult({
        studentPaperId: spId,
        questionId: evt.questionId,
        finalBin: evt.finalBin,
        earnedMarks: evt.earnedMarks,
        attemptsUsed: evt.attemptsUsed,
        overTime: evt.overTime,
        scaffoldActive: evt.scaffoldActive,
      });
    });

    source.addEventListener('report-ready', () => {
      navigate(`/reports/${spId}`);
    });

    source.addEventListener('answer-review', (event) => {
      const attempt = JSON.parse((event as MessageEvent<string>).data) as Attempt;
      recordAttempt(attempt);
    });

    return () => {
      source.close();
      setConnected(false);
    };
  }, [spId, navigate, recordResult, recordAttempt, setOpenLadderGrade]);

  return { connected, degraded };
}
